/**
 * The slug the server will derive from a title, computed here so the editor can show it while the
 * title is still being typed.
 *
 * This mirrors `SlugGenerator` in Danak.Application — the official Bulgarian transliteration (Закон
 * за транслитерацията), not a guess at one. The server remains the authority: it derives the slug
 * again on save and its answer is what is kept. If the two tables ever disagree the preview is
 * wrong for a moment and nothing else happens, but they are not meant to, and `slug.test.ts` holds
 * the same cases as the C# tests so that a change to one is noticed in the other.
 */
const CYRILLIC: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k',
  л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h',
  ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sht', ъ: 'a', ь: 'y', ю: 'yu', я: 'ya',
};

export const MAX_SLUG_LENGTH = 100;

/** Stands in for the "a" of a word-final -ия while the text is still Cyrillic. */
const IA_MARKER = '';

export function slugify(text: string): string {
  // A word ending in -ия is written -ia: Sofia, not Sofiya. The only positional rule in the system.
  const lower = text.trim().toLowerCase().replace(/ия(?![\p{L}\p{Nd}])/gu, `и${IA_MARKER}`);

  let out = '';

  // Per character, and the Cyrillic table first. Normalising the whole string up front splits й
  // into и plus a combining breve, the breve is dropped as an accent, and every й becomes "i".
  for (const char of lower) {
    if (char === IA_MARKER) {
      out += 'a';
    } else if (CYRILLIC[char] !== undefined) {
      out += CYRILLIC[char];
    } else {
      for (const part of char.normalize('NFD')) {
        if (/\p{Mn}/u.test(part)) {
          continue;
        }
        out += /[a-z0-9]/.test(part) ? part : '-';
      }
    }
  }

  const slug = out.replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');

  if (slug.length <= MAX_SLUG_LENGTH) {
    return slug;
  }

  // Cut at a word, so the slug does not end in half of one.
  const cut = slug.slice(0, MAX_SLUG_LENGTH);
  const lastDash = cut.lastIndexOf('-');

  return (lastDash > MAX_SLUG_LENGTH / 2 ? cut.slice(0, lastDash) : cut).replace(/^-+|-+$/g, '');
}

export function isValidSlug(slug: string): boolean {
  return slug.length > 0 && slug.length <= 200 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}
